import { z } from "zod";
import { HerSkinSessionIdSchema, IsoDateTimeSchema, StableIdSchema } from "./common.js";

/**
 * 这些 Schema 冻结能力证据与附件上传边界。附件已有受控 BFF 路由；模型切换
 * 仍未注册。`write_not_exercised` 表示源码确认但尚未通过真实写探针验收。
 */
export const CapabilityVerificationSchema = z.enum([
  "live_readonly_verified",
  "source_verified",
  "write_not_exercised",
]);

export const AttachmentKindSchema = z.enum(["image", "video", "pdf", "file"]);
export const ATTACHMENT_MAX_BYTES = {
  image: 10 * 1024 * 1024,
  video: 20 * 1024 * 1024,
  pdf: 20 * 1024 * 1024,
  file: 20 * 1024 * 1024,
} as const;
export const ATTACHMENT_TEXT_PREVIEW_MAX_BYTES = 1024 * 1024;

export const AttachmentIdSchema = StableIdSchema;
export const AttachmentDeliveryStateSchema = z.enum([
  "not_sent",
  "reserved",
  "attached",
  "consumed",
  "failed",
  "unknown",
]);

export const AttachmentUploadMetadataSchema = z
  .object({
    sessionId: HerSkinSessionIdSchema,
    kind: AttachmentKindSchema,
    /** 浏览器使用 encodeURIComponent；文件名不进入 URL/access log。 */
    encodedFilename: z.string().trim().min(1).max(1024),
    mimeType: z.string().trim().min(1).max(160),
  })
  .strict();

export const AttachmentUploadIntentSchema = z
  .object({
    sessionId: HerSkinSessionIdSchema,
    kind: AttachmentKindSchema,
    filename: z.string().trim().min(1).max(255),
    mimeType: z.string().trim().min(1).max(160),
    sizeBytes: z
      .number()
      .int()
      .positive()
      .max(20 * 1024 * 1024),
  })
  .strict();

export const PreparedAttachmentSchema = z
  .object({
    attachmentId: AttachmentIdSchema,
    sessionId: HerSkinSessionIdSchema,
    kind: AttachmentKindSchema,
    filename: z.string().min(1).max(255),
    mimeType: z.string().min(1).max(160),
    sizeBytes: z
      .number()
      .int()
      .positive()
      .max(20 * 1024 * 1024),
    createdAt: IsoDateTimeSchema,
    deliveryState: z.literal("not_sent"),
  })
  .strict();

/**
 * 消息历史只公开已经随 Prompt 明确发送成功的附件。URL 是 HerSkin 同源受控路由，
 * 不包含磁盘路径、Hermes 内部路径或任何认证凭证。
 */
export const MessageAttachmentSchema = z
  .object({
    attachmentId: AttachmentIdSchema,
    kind: AttachmentKindSchema,
    filename: z.string().min(1).max(255),
    mimeType: z.string().min(1).max(160),
    sizeBytes: z
      .number()
      .int()
      .positive()
      .max(20 * 1024 * 1024),
    state: z.literal("sent"),
    presentation: z.enum(["image", "video", "text", "file"]),
    available: z.boolean(),
    contentUrl: z.string().startsWith("/").max(512).nullable(),
    downloadUrl: z.string().startsWith("/").max(512).nullable(),
    createdAt: IsoDateTimeSchema,
  })
  .strict();

export const DiscardAttachmentResponseSchema = z
  .object({
    attachmentId: AttachmentIdSchema,
    discarded: z.boolean(),
  })
  .strict();

export const ContextUsageSchema = z
  .object({
    /** Hermes state.db 中该 Stored Session 的持久累计值。 */
    inputTokens: z.number().int().min(0),
    outputTokens: z.number().int().min(0),
    cacheReadTokens: z.number().int().min(0),
    cacheWriteTokens: z.number().int().min(0),
    reasoningTokens: z.number().int().min(0),
    totalTokens: z.number().int().min(0),
    apiCalls: z.number().int().min(0),
    /** 运行时重建后、持久会话读取失败时只能明确标记为 runtime_process。 */
    cumulativeMeasurement: z.enum(["stored_session", "runtime_process"]),
    contextUsed: z.number().int().min(0).nullable(),
    contextMax: z.number().int().positive().nullable(),
    contextPercent: z.number().min(0).max(100).nullable(),
    /** Hermes 无真实 current-window occupancy 时必须返回 unknown，禁止用累计 Token 伪造。 */
    occupancyMeasurement: z.enum(["live_window", "unknown"]),
    compressions: z.number().int().min(0).nullable(),
    activeSubagents: z.number().int().min(0).nullable(),
  })
  .strict()
  .superRefine((usage, context) => {
    const occupancy = [usage.contextUsed, usage.contextMax, usage.contextPercent];
    const hasEveryOccupancyValue = occupancy.every((value) => value !== null);
    const hasAnyOccupancyValue = occupancy.some((value) => value !== null);
    if (usage.occupancyMeasurement === "live_window" && !hasEveryOccupancyValue) {
      context.addIssue({
        code: "custom",
        path: ["occupancyMeasurement"],
        message: "真实窗口占用必须同时包含 used、max 和 percent",
      });
    }
    if (usage.occupancyMeasurement === "unknown" && hasAnyOccupancyValue) {
      context.addIssue({
        code: "custom",
        path: ["occupancyMeasurement"],
        message: "未知窗口占用不得夹带估算数值",
      });
    }
  });

export const SessionRuntimeAvailabilitySchema = z.enum([
  "live",
  "stored_only",
  "not_started",
  "adapter_unavailable",
  "unavailable",
]);

/**
 * 浏览器只接收模型、Token 和运行计数白名单。Hermes 的 cwd、system_prompt、
 * tools、skills、内部 Session runtime ID 等字段必须在 Adapter 边界被剥离。
 */
export const SessionRuntimeStateSchema = z
  .object({
    sessionId: HerSkinSessionIdSchema,
    availability: SessionRuntimeAvailabilitySchema,
    model: z
      .object({
        provider: z.string().trim().min(1).max(120).nullable(),
        model: z.string().trim().min(1).max(240),
      })
      .strict()
      .nullable(),
    usage: ContextUsageSchema.nullable(),
    observedAt: IsoDateTimeSchema,
  })
  .strict()
  .superRefine((state, context) => {
    if (state.availability === "live" && !state.usage) {
      context.addIssue({
        code: "custom",
        path: ["usage"],
        message: "live 状态必须携带 Hermes 用量",
      });
    }
    if (
      ["not_started", "adapter_unavailable", "unavailable"].includes(state.availability) &&
      state.usage
    ) {
      context.addIssue({
        code: "custom",
        path: ["usage"],
        message: "不可用状态不得携带伪造用量",
      });
    }
  });

export const SessionModelStateSchema = z
  .object({
    sessionId: HerSkinSessionIdSchema,
    provider: z.string().min(1).max(120),
    model: z.string().min(1).max(240),
    context: ContextUsageSchema.nullable(),
    verification: CapabilityVerificationSchema,
  })
  .strict();

export const ModelOptionSchema = z
  .object({
    provider: z.string().min(1).max(120),
    model: z.string().min(1).max(240),
    label: z.string().min(1).max(240),
    contextMax: z.number().int().positive().nullable(),
  })
  .strict();

export type CapabilityVerification = z.infer<typeof CapabilityVerificationSchema>;
export type AttachmentKind = z.infer<typeof AttachmentKindSchema>;
export type AttachmentDeliveryState = z.infer<typeof AttachmentDeliveryStateSchema>;
export type AttachmentUploadMetadata = z.infer<typeof AttachmentUploadMetadataSchema>;
export type AttachmentUploadIntent = z.infer<typeof AttachmentUploadIntentSchema>;
export type PreparedAttachment = z.infer<typeof PreparedAttachmentSchema>;
export type MessageAttachment = z.infer<typeof MessageAttachmentSchema>;
export type DiscardAttachmentResponse = z.infer<typeof DiscardAttachmentResponseSchema>;
export type ContextUsage = z.infer<typeof ContextUsageSchema>;
export type SessionRuntimeAvailability = z.infer<typeof SessionRuntimeAvailabilitySchema>;
export type SessionRuntimeState = z.infer<typeof SessionRuntimeStateSchema>;
export type SessionModelState = z.infer<typeof SessionModelStateSchema>;
export type ModelOption = z.infer<typeof ModelOptionSchema>;
